import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Edit, ShieldCheck, BadgeCheck, MapPin } from "lucide-react";
import ProfileWizard from "@/components/ProfileWizard";
import { profileMainImage, onImageError } from "@/lib/img";

export default function ProfileDashboard({ role }) {
  const { user, refresh } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/my/profiles");
      setProfiles(data.profiles);
    } catch (e) { console.warn("[ProfileDashboard] load error:", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!confirm("Delete this profile?")) return;
    try { await api.delete(`/profiles/${id}`); toast.success("Deleted"); load(); } catch (e) { console.warn("[ProfileDashboard.jsx] non-blocking error:", e); }
  };

  const openCreate = () => { setEditingProfile(null); setWizardOpen(true); };
  const openEdit = (p) => { setEditingProfile(p); setWizardOpen(true); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid={`${role}-dashboard`}>
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <p className="text-overline">{role === "agency" ? "Agency" : "Independent"}</p>
          <h1 className="font-heading text-4xl text-white mt-2 font-light">My profiles</h1>
          <p className="text-sm text-[#a3a3a3] mt-2">{profiles.length} profile{profiles.length === 1 ? "" : "s"}</p>
        </div>
        <button onClick={openCreate} className="btn-gold rounded-md px-4 py-2.5 text-sm inline-flex items-center gap-2" data-testid="create-profile-btn">
          <Plus className="w-4 h-4" /> New profile
        </button>
      </div>

      {profiles.length === 0 ? (
        <div className="card-luxe rounded-lg p-12 text-center">
          <p className="font-heading text-2xl text-white">No profiles yet</p>
          <p className="text-sm text-[#a3a3a3] mt-2 mb-5">Walk through the wizard to publish your first profile.</p>
          <button onClick={openCreate} className="btn-gold rounded-md px-5 py-2.5 text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Start now</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {profiles.map((p) => (
            <div key={p.id} className="card-luxe rounded-lg p-5" data-testid={`my-profile-${p.id}`}>
              <div className="flex gap-4">
                <img src={profileMainImage(p)} onError={onImageError} alt={p.name} className="w-24 h-32 object-cover rounded" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-heading text-xl text-white truncate">{p.name}</p>
                    {p.verification_level === "verified" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300 border border-emerald/40 px-1.5 py-0.5 rounded-full whitespace-nowrap"><BadgeCheck className="w-3 h-3" /> Verified</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#a3a3a3] border border-white/10 px-1.5 py-0.5 rounded-full whitespace-nowrap">Basic</span>
                    )}
                  </div>
                  <p className="text-xs text-[#a3a3a3] inline-flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {p.city}{p.country_name ? `, ${p.country_name}` : ""}</p>
                  <p className="text-xs text-gold mt-1">{p.price_from ? `from ${p.price_from} ${p.currency || "EUR"}` : "Rates not set"}</p>
                  <p className="text-xs text-[#737373] mt-2">★ {p.rating?.toFixed?.(1) || "—"} · {p.review_count || 0} reviews · Trust {p.trust_score || 0}</p>
                </div>
              </div>
              <div className="flex gap-2 mt-4 flex-wrap">
                <button onClick={() => openEdit(p)} className="btn-ghost-dark rounded px-3 py-1.5 text-xs inline-flex items-center gap-1.5" data-testid={`edit-${p.id}`}><Edit className="w-3 h-3" /> Edit in wizard</button>
                <Link to={`/profile/${p.id}`} className="btn-ghost-dark rounded px-3 py-1.5 text-xs inline-flex items-center gap-1.5">View public</Link>
                <Link to="/verification" className="bg-emerald/10 border border-emerald/30 text-emerald-300 rounded px-3 py-1.5 text-xs inline-flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Verify</Link>
                <button onClick={() => remove(p.id)} className="text-red-400 px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ml-auto"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {wizardOpen && (
        <ProfileWizard
          role={role}
          profile={editingProfile}
          onClose={() => setWizardOpen(false)}
          onSaved={() => { load(); refresh?.(); }}
        />
      )}
    </div>
  );
}
